/*
Bounded, de-duplicating runner for the download engine's per-video offline
caching (transcripts, chapters, posters).

Both properties exist because of the same shape of failure, measured on a
fresh install of the funder's 184-video round:

  - **Unbounded fan-out.** Each backfill walked every completed video and
    fired its fetch immediately, so one sweep put ~50 `getTranscriptContent`
    requests on the wire inside two seconds — each answered with a full
    transcript body that is parsed on the main thread and then written across
    the bridge to a native file. A video tap landing in that window took 3.3s
    to reach a playing frame, against 1.6s for the same tap on a quiet app.
    The work itself is legitimate and has to happen; doing all of it at once
    is what makes the app unusable while it does.

  - **No de-duplication.** Only the poster path tracked its in-flight ids, so
    a video reachable by two triggers (its own download completing, and a
    sweep that includes it) fetched the same transcript twice — visible in the
    device log as pairs of identical requests in the same millisecond.

Dropping a duplicate rather than queueing it is deliberate, and matches what
the poster path already did: every caller is a "cache this if it still needs
caching" request whose guard re-reads the store when it runs, so the in-flight
attempt already produces the outcome the duplicate wanted. The cost of the
drop is one skipped refresh in a genuine racing-change case (a poster replaced
mid-fetch), which the next sweep picks up.

Deliberately NOT a general-purpose scheduler: no priorities, no cancellation,
no result plumbing. Every caller is fire-and-forget best-effort work whose
failures are already swallowed at the call site.
*/

/** How many of one kind of caching task may be in flight at once. */
export const DEFAULT_MEDIA_CACHE_CONCURRENCY = 3;

export class MediaCacheQueue {
  private readonly limit: number;
  // Ids queued or running. One set for both states so a duplicate is dropped
  // whether the original is waiting or already fetching.
  private readonly claimed = new Set<string>();
  private readonly waiting: { id: string; task: () => Promise<void> }[] = [];
  private running = 0;

  constructor(limit: number = DEFAULT_MEDIA_CACHE_CONCURRENCY) {
    // A non-positive limit would silently park every task forever, which for
    // this queue means offline caching stops with no error anywhere.
    this.limit = Math.max(1, limit);
  }

  /**
   * Run `task` for `id` unless one is already queued or in flight for it.
   * Fire-and-forget: the returned void is not a completion signal.
   */
  run(id: string, task: () => Promise<void>): void {
    if (this.claimed.has(id)) {
      return;
    }

    this.claimed.add(id);
    this.waiting.push({ id, task });
    this.drain();
  }

  /** Queued + running, for tests and the engine's summary logging. */
  get pending(): number {
    return this.claimed.size;
  }

  private drain(): void {
    while (this.running < this.limit && this.waiting.length > 0) {
      const next = this.waiting.shift();

      if (next == null) {
        return;
      }

      this.running += 1;
      // The task's own errors are already swallowed by its caller, but a
      // throw here must never leak the slot — an exhausted limit would stop
      // all offline caching for the rest of the session.
      void next
        .task()
        .catch(() => {})
        .finally(() => {
          this.running -= 1;
          this.claimed.delete(next.id);
          this.drain();
        });
    }
  }
}
